import { useState } from "react";
import { Users, ArrowLeft } from "lucide-react";
import { Modal, ModalScroll } from "./Modal";
import { AnonSilhouette } from "./FestivalGraphics";
import { resolveUrl } from "../../lib/media";

const visibleJury = (jury) => (jury || []).filter((m) => m && !m.hidden && (m.name || m.photo));

// Public "Jury" modal for a festival edition. Clicking a member opens their bio.
// `colorMode`: "color" (always colour) or "hover" (B&W → colour on hover).
export const FestivalJuryModal = ({ edition, colorMode = "hover", onClose }) => {
  const members = visibleJury(edition?.jury);
  const [active, setActive] = useState(null);
  const imgFilter = colorMode === "color" ? "" : "grayscale hover:grayscale-0";
  const close = () => { setActive(null); onClose?.(); };

  return (
    <Modal open={!!edition} onClose={close} size="2xl" testid="festival-jury-modal">
      {edition && (
        <ModalScroll testid="festival-jury-modal-scroll">
          <div className="p-6 md:p-10">
            {active ? (
              <div className="flex flex-col sm:flex-row gap-6" data-testid="festival-jury-member-detail">
                <div className="w-full sm:w-48 shrink-0">
                  <button onClick={() => setActive(null)} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-white/60 hover:text-electric mb-4" data-testid="festival-jury-back"><ArrowLeft size={14} /> Retour</button>
                  <div className="aspect-[3/4] rounded-xl overflow-hidden border border-white/10 bg-[#0e0e0e]">
                    {active.photo ? <img src={resolveUrl(active.photo)} alt={active.name} className="w-full h-full object-cover" /> : <AnonSilhouette className="w-full h-full" />}
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-serif text-3xl text-white leading-tight">{active.name}</h3>
                  {active.role && <div className="text-[11px] tracking-[0.16em] uppercase text-electric mt-2">{active.role}</div>}
                  {active.bio
                    ? <div className="mt-5 space-y-3 text-white/75 font-light leading-relaxed" data-testid="festival-jury-member-bio">{String(active.bio).split(/\n{2,}/).map((p, i) => <p key={i}>{p.trim()}</p>)}</div>
                    : <p className="mt-5 text-secondary2 font-light">Biographie à venir.</p>}
                </div>
              </div>
            ) : (
              <>
                <div className="text-[11px] tracking-[0.3em] uppercase text-electric mb-3 pr-10 inline-flex items-center gap-2">
                  <Users size={14} /> Le jury
                </div>
                <h2 data-testid="festival-jury-modal-title" className="font-serif text-3xl md:text-4xl text-white tracking-tight leading-[0.98] mb-8">
                  Karams Festival {edition.year}{edition.title ? <span className="text-white/50 font-light"> — {edition.title}</span> : null}
                </h2>
                {members.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5 md:gap-7">
                    {members.map((m, i) => (
                      <button key={m.id || i} onClick={() => setActive(m)} className="text-center group" data-testid={`festival-jury-member-${i}`}>
                        <div className="aspect-square w-full rounded-xl overflow-hidden border border-white/10 bg-[#0e0e0e] mb-3 group-hover:border-electric/50 transition-colors">
                          {m.photo
                            ? <img src={resolveUrl(m.photo)} alt={m.name} loading="lazy" className={`w-full h-full object-cover transition-[filter] duration-500 ${imgFilter}`} />
                            : <AnonSilhouette className="w-full h-full" />}
                        </div>
                        <div className="text-sm text-white leading-snug group-hover:text-electric transition-colors">{m.name}</div>
                        {m.role && <div className="text-[11px] tracking-[0.12em] uppercase text-electric mt-1">{m.role}</div>}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-secondary2 font-light">Le jury de cette édition sera dévoilé prochainement.</p>
                )}
              </>
            )}
          </div>
        </ModalScroll>
      )}
    </Modal>
  );
};
